/* Povellea package cart UI — 2026-07-23 */
(() => {
  'use strict';

  const VERSION = '20260810-01-simple-bundles';
  const MEMBER_SELECTOR = '.cart-item--package-member[data-package-instance]';

  // Translated UI strings from layout/theme.liquid, with English literal
  // fallbacks so a missing global can never blank a label.
  const I18N = (window.PovelleaI18n && window.PovelleaI18n.pkg) || {};
  const t = (key, fallback) => (typeof I18N[key] === 'string' ? I18N[key] : fallback);
  const tCount = (key, fallback, n) => t(key, fallback).replace('__COUNT__', n);

  const existing = window.PovelleaPackageCart;
  if (existing?.version === VERSION) {
    existing.refresh();
    return;
  }

  const state = {
    refreshFrame: 0,
    observer: null,
    accordionBound: false,
    busyInstances: new Set()
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const formatMoney = (cents) => {
    const amount = Math.max(0, Number(cents || 0)) / 100;
    return new Intl.NumberFormat('en-AE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const moneyMarkup = (cents) => `
    <span class="uae-dirham-price">
      <span class="uae-dirham-sr-only">${escapeHtml(t('currencySr', 'AED '))}</span>
      <span>${formatMoney(cents)}</span>
    </span>
  `;

  // The package-aware count and the header write both live in global.js now.
  // This file kept private copies of each, and the header copy never touched
  // [hidden] - so once the count reached zero it wrote the text "0" into a span
  // that was still visible, which is the transient/sticky "0" after removing the
  // last package. Delegate instead of duplicating.
  // Optional-chained on purpose. global.js is loaded first and unconditionally
  // from theme.liquid, so this resolves in practice; the guard only means a
  // load-order surprise degrades the count instead of throwing partway through a
  // cart mutation and skipping the cart:refresh dispatch that follows it.
  const logicalCartCount = (cart) => window.PovelleaCartCount?.logical(cart) ?? 0;

  // Drawer-local "(N)" beside the drawer heading. Separate from the header badge
  // on purpose: this one may be derived from the drawer's own DOM, the header
  // badge may not.
  const applyDrawerCount = (count) => {
    document.querySelectorAll('[data-pp-logical-cart-count]').forEach((node) => {
      node.textContent = node.hasAttribute('data-pp-count-parentheses') ? `(${count})` : String(count);
    });
  };

  // Cart JSON is authoritative, so this path may write the header badge.
  const syncCartCount = (cart) => {
    const count = logicalCartCount(cart);
    applyDrawerCount(count);
    window.PovelleaCartCount?.apply(count);
  };

  // Runs from a rAF after the drawer DOM mutates, so it can observe a half
  // re-rendered drawer. It therefore updates the drawer's own "(N)" only and
  // deliberately does NOT write the header badge: every path that mutates the
  // cart already writes the header from the cart JSON before the drawer is
  // re-rendered, and a DOM-derived second writer racing behind it is what made
  // the header disagree with the cart.
  const syncCartCountFromDrawer = () => {
    const surface = document.querySelector('#CartDrawer .drawer__cart-items-wrapper');
    if (!surface) return;

    const packages = surface.querySelectorAll(':scope > .pp-package-group').length;
    const standaloneCount = Array.from(surface.querySelectorAll(':scope > .cart-item:not(.cart-item--package-member)'))
      .reduce((sum, item) => {
        const quantity = Number(item.querySelector('.quantity__input')?.value || 1);
        return sum + Math.max(0, quantity);
      }, 0);
    applyDrawerCount(packages + standaloneCount);
  };

  const injectStyles = () => {
    if (document.getElementById('PovelleaPackageCartStyles')) return;

    const style = document.createElement('style');
    style.id = 'PovelleaPackageCartStyles';
    style.textContent = `
      .pp-package-group {
        position: relative;
        margin: 0 0 16px;
        border: 1px solid var(--drawer-border, var(--cart-border-color, #d4dbd5));
        border-radius: 14px;
        background: #fff;
        overflow: hidden;
      }

      .pp-package-group__card {
        padding: 18px 18px 16px;
      }

      /*
       * A member's own cart-line markup is kept in the DOM - the remove and
       * quantity controls and their data attributes are what the cart API
       * calls below read - but the package card is what is seen. Hiding by
       * exception keeps this correct on both surfaces, whose line markup
       * differs.
       *
       * The wrapper is emptied by that rule but is still a cart line as far as
       * the cart's own stylesheet is concerned, so it kept drawing its card:
       * 16px of padding and a 1px border around nothing, a 34px rectangle
       * above the actions. Both surfaces style .cart-item from their own
       * section id, which no selector here can out-rank without guessing at
       * those ids, so the box is zeroed outright - the same reason the rule
       * above is !important.
       */
      .pp-package-group .cart-item--package-member {
        display: block !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
      }

      .pp-package-group .cart-item--package-member > *:not(.pv-pkg):not(.pv-pkg__row) {
        display: none !important;
      }

      .pp-package-group .discounts {
        display: none !important;
      }

      .pp-package-group__actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        padding: 14px;
        background: rgba(255, 255, 255, .28);
        border-top: 1px solid var(--drawer-border, var(--cart-border-color, #d4dbd5));
      }

      .pp-package-group__action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 42px;
        padding: 10px 14px;
        color: inherit;
        font: inherit;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        letter-spacing: .06em;
        text-align: center;
        text-decoration: none;
        text-transform: uppercase;
        background: transparent;
        border: 1px solid currentColor;
        border-radius: 10px;
        cursor: pointer;
      }

      .pp-package-group__action {
        gap: 8px;
      }

      /* The outlined action inherited plain black from the document. It takes
         the brand olive, so its outline and its label match the filled action
         beside it. */
      .pp-package-group__action--remove {
        color: #434832;
      }

      .pp-package-group__action--edit {
        color: #fff;
        background: var(--drawer-accent, var(--cart-accent-color, #434832));
        border-color: var(--drawer-accent, var(--cart-accent-color, #434832));
      }

      .pp-package-group__action:disabled {
        cursor: wait;
        opacity: .55;
      }

      .pp-package-group__status {
        grid-column: 1 / -1;
        min-height: 0;
        margin: 0;
        color: #a12424;
        font-size: 11px;
        line-height: 1.35;
      }

      .pp-package-group__status:empty {
        display: none;
      }

    `;
    document.head.appendChild(style);
  };

  const packageEditUrl = (member, instance) => {
    const configured = String(member.dataset.packageUrl || '').trim();
    const fallback = String(member.dataset.packageFallbackUrl || '').trim();
    const base = configured || fallback;
    if (!base) return '';

    try {
      const url = new URL(base, window.location.origin);
      url.searchParams.set('edit_package', instance);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      const separator = base.includes('?') ? '&' : '?';
      return `${base}${separator}edit_package=${encodeURIComponent(instance)}`;
    }
  };

  const showMemberQuantity = (member) => {
    if (!member || member.querySelector('.pp-package-item-quantity')) return;

    const quantity = Math.max(1, Number(member.dataset.packagePieceCount || member.dataset.packageQuantity || 1));
    const quantityLabel = document.createElement('span');
    quantityLabel.className = 'pp-package-item-quantity';
    quantityLabel.textContent = member.dataset.packagePieceCount ? tCount('piecesCount', 'Pieces: __COUNT__', quantity) : tCount('qtyCount', 'Qty: __COUNT__', quantity);
    quantityLabel.setAttribute('aria-label', member.dataset.packagePieceCount ? tCount('packagePiecesCount', 'Package pieces: __COUNT__', quantity) : tCount('quantityCount', 'Quantity: __COUNT__', quantity));

    const options = member.querySelector('.product-option__group');
    const title = member.querySelector('.cart-item__name');
    const anchor = options || title;

    if (anchor) {
      anchor.insertAdjacentElement('afterend', quantityLabel);
    } else {
      member.querySelector('.cart-item__details, .cart-item__info')?.appendChild(quantityLabel);
    }
  };

  const buildGroup = (instance, members) => {
    const first = members[0];
    const packageName = first.dataset.packageName || t('package', 'Package');
    const editUrl = packageEditUrl(first, instance);
    const totalQuantity = members.reduce((sum, member) => {
      return sum + Math.max(0, Number(member.dataset.packagePieceCount || member.dataset.packageQuantity || 0));
    }, 0);
    const regularTotal = members.reduce((sum, member) => sum + Math.max(0, Number(member.dataset.packageRegularPrice || 0)), 0);
    const finalTotal = members.reduce((sum, member) => sum + Math.max(0, Number(member.dataset.packageFinalPrice || 0)), 0);

    const group = document.createElement('section');
    group.className = 'pp-package-group';
    group.dataset.packageGroup = instance;
    group.setAttribute('aria-label', packageName);

    /*
     * The card itself is rendered by snippets/pv-package-card.liquid on the
     * parent line, because only Liquid can reach each component product's own
     * gallery for its thumbnail. This function keeps everything it always did -
     * grouping by instance, the totals, the edit and remove actions - and now
     * lifts that card out of the line and fills its figures in from every
     * member, so a package split across several cart lines still reports one
     * piece count and one price.
     */
    const cardHost = members.find((member) => member.querySelector('[data-pv-pkg-card]'));
    const card = cardHost ? cardHost.querySelector('[data-pv-pkg-card]') : null;

    group.innerHTML = `
      ${card ? '<div class="pp-package-group__card"></div>' : `
      <div class="pp-package-group__header">
        <div>
          <span class="pp-package-group__eyebrow">${t('package', 'Package')}</span>
          <h3 class="pp-package-group__title">${escapeHtml(packageName)}</h3>
          <p class="pp-package-group__meta">${totalQuantity} ${totalQuantity === 1 ? t('item', 'item') : t('items', 'items')}</p>
        </div>
        <div class="pp-package-group__price">
          ${regularTotal > finalTotal ? `<s class="pp-package-group__old-price">${moneyMarkup(regularTotal)}</s>` : ''}
          <strong class="pp-package-group__final-price">${moneyMarkup(finalTotal)}</strong>
        </div>
      </div>`}
      <div class="pp-package-group__items"></div>
      <div class="pp-package-group__actions">
        ${editUrl ? `<a class="pp-package-group__action pp-package-group__action--edit" href="${escapeHtml(editUrl)}">${t('editPackage', 'Edit Package')}</a>` : ''}
        <button class="pp-package-group__action pp-package-group__action--remove" type="button" data-remove-package="${escapeHtml(instance)}" aria-label="${t('removePackageNamed', 'Remove __NAME__ from cart').replace('__NAME__', escapeHtml(packageName))}">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false" width="15" height="15">
            <path d="M4 6h12M8.2 6V4.8A1.3 1.3 0 0 1 9.5 3.5h1a1.3 1.3 0 0 1 1.3 1.3V6M6.2 6l.6 9.1a1.4 1.4 0 0 0 1.4 1.3h3.6a1.4 1.4 0 0 0 1.4-1.3L13.8 6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>${t('remove', 'Remove')}</span>
        </button>
        <p class="pp-package-group__status" data-package-action-status role="status" aria-live="polite"></p>
      </div>
    `;

    if (card) {
      group.querySelector('.pp-package-group__card').appendChild(card);

      /*
       * Extras and add-ons are their own cart lines. Each renders one row from
       * its own product, and they join the package's list here so the card
       * reads as the whole package rather than the bundle line alone.
       */
      const rowsHost = card.querySelector('[data-pv-pkg-rows]');
      if (rowsHost) {
        members.forEach((member) => {
          const row = member.querySelector('[data-pv-pkg-row]');
          if (row) rowsHost.appendChild(row);
        });
      }

      /*
       * The figures are recomputed from every member rather than left at what
       * the parent line alone knew, so a customised package is counted and
       * priced as one thing. The chip falls back to Liquid's own number when a
       * package somehow reports no quantity at all.
       */
      const chip = card.querySelector('.pv-pkg__chip');
      if (chip && totalQuantity > 0) {
        chip.textContent = `${totalQuantity} ${totalQuantity === 1 ? t('piece', 'PIECE') : t('pieces', 'PIECES')}`;
      }

      const now = card.querySelector('.pv-pkg__now');
      if (now && finalTotal > 0) now.innerHTML = moneyMarkup(finalTotal);

      const was = card.querySelector('.pv-pkg__was');
      const save = card.querySelector('.pv-pkg__save');
      const hasSaving = regularTotal > finalTotal && finalTotal > 0;
      if (was) {
        was.hidden = !hasSaving;
        if (hasSaving) was.innerHTML = moneyMarkup(regularTotal);
      }
      if (save) {
        const percent = hasSaving ? Math.round(((regularTotal - finalTotal) / regularTotal) * 100) : 0;
        save.hidden = !hasSaving || percent <= 0;
        if (!save.hidden) save.textContent = t('savePercent', 'SAVE __PERCENT__%').replace('__PERCENT__', percent);
      }
    }

    const itemsContainer = group.querySelector('.pp-package-group__items');
    members.forEach((member) => {
      member.dataset.packageGrouped = VERSION;
      if (!card) showMemberQuantity(member);
      itemsContainer.appendChild(member);
    });

    return group;
  };

  const groupSurface = (surface) => {
    if (!surface) return;
    const members = Array.from(surface.querySelectorAll(`:scope > ${MEMBER_SELECTOR}:not([data-package-grouped])`));
    if (!members.length) return;

    const groups = new Map();
    members.forEach((member) => {
      const instance = String(member.dataset.packageInstance || '').trim();
      if (!instance) return;
      if (!groups.has(instance)) groups.set(instance, []);
      groups.get(instance).push(member);
    });

    groups.forEach((groupMembers, instance) => {
      const first = groupMembers[0];
      const marker = document.createComment(`package:${instance}`);
      first.parentNode.insertBefore(marker, first);
      const group = buildGroup(instance, groupMembers);
      marker.replaceWith(group);
    });
  };

  /*
   * The contents panel. Delegated once at the document, so a card that arrives
   * with a re-rendered drawer needs no rebinding. Space and Enter come free:
   * the control is a real <button>.
   */
  const bindAccordion = () => {
    if (state.accordionBound) return;
    state.accordionBound = true;

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-pv-pkg-toggle]');
      if (!toggle) return;

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));

      const rows = document.getElementById(toggle.getAttribute('aria-controls'));
      if (rows) rows.hidden = expanded;
    });
  };

  const refresh = () => {
    injectStyles();
    bindAccordion();
    document.querySelectorAll('#CartDrawer .drawer__cart-items-wrapper, .main-cart .cart-items').forEach(groupSurface);
    syncCartCountFromDrawer();
  };

  const scheduleRefresh = () => {
    if (state.refreshFrame) return;
    state.refreshFrame = requestAnimationFrame(() => {
      state.refreshFrame = 0;
      refresh();
    });
  };

  const fetchJson = async (url, options) => {
    const response = await fetch(url, Object.assign({
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }, options || {}));
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || `Cart request failed (${response.status}).`);
    return payload;
  };

  const refreshCartSurfaces = async (cart) => {
    syncCartCount(cart);

    const detail = { cart, source: 'povellea-package-cart' };
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));

    const drawerWasOpen = Boolean(
      document.querySelector('cart-drawer.active, cart-drawer[open], #CartDrawer.active')
    );

    try {
      const response = await fetch(window.location.href, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Cart HTML refresh failed.');

      const html = await response.text();
      const documentCopy = new DOMParser().parseFromString(html, 'text/html');

      const currentDrawer = document.querySelector('cart-drawer');
      const freshDrawer = documentCopy.querySelector('cart-drawer');
      if (currentDrawer && freshDrawer) currentDrawer.replaceWith(freshDrawer);

      const currentMainCart = document.querySelector('.main-cart[data-pp-cart-version]');
      const freshMainCart = documentCopy.querySelector('.main-cart[data-pp-cart-version]');
      if (currentMainCart && freshMainCart) currentMainCart.replaceWith(freshMainCart);

      scheduleRefresh();
      window.PovelleaCartDrawer?.refresh?.();

      if (drawerWasOpen) {
        requestAnimationFrame(() => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return;
          if (typeof drawer.open === 'function') {
            drawer.open();
          } else {
            drawer.classList.add('active');
            drawer.setAttribute('open', '');
            drawer.setAttribute('aria-hidden', 'false');
          }
        });
      }
    } catch (error) {
      console.error('[Povellea package cart] UI refresh failed.', error);
      window.location.reload();
    }
  };

  const removePackage = async (instance, button) => {
    if (!instance || state.busyInstances.has(instance)) return;
    state.busyInstances.add(instance);

    const group = button.closest('.pp-package-group');
    const status = group?.querySelector('[data-package-action-status]');
    const buttons = Array.from(group?.querySelectorAll('.pp-package-group__action') || []);
    buttons.forEach((control) => {
      control.setAttribute('aria-disabled', 'true');
      if ('disabled' in control) control.disabled = true;
    });
    if (status) status.textContent = '';
    button.dataset.originalLabel ||= button.textContent;
    button.textContent = t('removing', 'Removing…');

    try {
      const cart = await fetchJson(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const targetLines = (cart.items || []).filter((line) => {
        return String(line.properties?._package_instance || '') === instance;
      });
      if (!targetLines.length) throw new Error(t('notInCart', 'This package is no longer in the cart.'));

      const updates = {};
      targetLines.forEach((line) => {
        updates[line.key] = 0;
      });

      const removedCodes = new Set(
        targetLines
          .map((line) => String(line.properties?._package_discount_code || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const requiredCodes = new Set(
        (cart.items || [])
          .filter((line) => String(line.properties?._package_instance || '') !== instance)
          .map((line) => String(line.properties?._package_discount_code || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const remainingCodes = (cart.discount_codes || [])
        .map((entry) => String(entry.code || '').trim())
        .filter((code) => code && (!removedCodes.has(code.toLowerCase()) || requiredCodes.has(code.toLowerCase())));

      const updatedCart = await fetchJson(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        body: JSON.stringify({
          updates,
          discount: remainingCodes.join(',')
        })
      });

      await refreshCartSurfaces(updatedCart);
    } catch (error) {
      console.error('[Povellea package cart] Remove failed.', error);
      if (status) status.textContent = error.message || t('removeFailed', 'Could not remove this package. Please try again.');
      button.textContent = button.dataset.originalLabel || t('removePackage', 'Remove package');
      buttons.forEach((control) => {
        control.removeAttribute('aria-disabled');
        if ('disabled' in control) control.disabled = false;
      });
    } finally {
      state.busyInstances.delete(instance);
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-remove-package]');
    if (!button) return;
    event.preventDefault();
    removePackage(String(button.dataset.removePackage || ''), button);
  });

  state.observer = new MutationObserver((mutations) => {
    const needsRefresh = mutations.some((mutation) => {
      return Array.from(mutation.addedNodes || []).some((node) => {
        return node.nodeType === 1 && (
          node.matches?.(MEMBER_SELECTOR)
          || node.querySelector?.(MEMBER_SELECTOR)
        );
      });
    });
    if (needsRefresh) scheduleRefresh();
  });

  const start = () => {
    injectStyles();
    refresh();
    state.observer.observe(document.body, { childList: true, subtree: true });
  };

  window.PovelleaPackageCart = {
    version: VERSION,
    refresh: scheduleRefresh,
    logicalCartCount,
    syncCartCount
  };


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
